import { Colors } from "@/constants/Colors";
import { AntDesign } from "@expo/vector-icons";
import React, { useEffect } from "react";
import {
  Dimensions,
  Keyboard,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";

const { height: SCREEN_HEIGHT } = Dimensions.get("window");

interface BottomSheetProps {
  isVisible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: number;
  enableBackdropDismiss?: boolean;
  enableSwipeDown?: boolean;
  title?: string;
  enableKeyboardAvoidance?: boolean;
  scrollable?: boolean;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isVisible,
  onClose,
  children,
  height = SCREEN_HEIGHT * 0.55,
  enableBackdropDismiss = true,
  enableSwipeDown = true,
  title,
  enableKeyboardAvoidance = true,
  scrollable = false,
}) => {
  const translateY = useSharedValue(height);
  const opacity = useSharedValue(0);
  const gestureY = useSharedValue(0);
  const keyboardOffset = useSharedValue(0);

  useEffect(() => {
    const keyboardWillShow = (e: any) => {
      if (enableKeyboardAvoidance && isVisible) {
        const keyboardHeight = e.endCoordinates.height;
        // More conservative offset to prevent layout breakage
        const offset = Math.min(keyboardHeight, 100);
        keyboardOffset.value = withTiming(-offset, { duration: 250 });
      }
    };

    const keyboardWillHide = () => {
      if (enableKeyboardAvoidance) {
        keyboardOffset.value = withTiming(0, { duration: 250 });
      }
    };

    const showEvent =
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent =
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

    const showSubscription = Keyboard.addListener(showEvent, keyboardWillShow);
    const hideSubscription = Keyboard.addListener(hideEvent, keyboardWillHide);

    return () => {
      showSubscription?.remove();
      hideSubscription?.remove();
    };
  }, [enableKeyboardAvoidance, isVisible, keyboardOffset]);

  useEffect(() => {
    if (isVisible) {
      translateY.value = withTiming(0, { duration: 300 });
      opacity.value = withTiming(1, { duration: 300 });
    } else {
      translateY.value = withTiming(height, { duration: 250 });
      opacity.value = withTiming(0, { duration: 250 });
      keyboardOffset.value = withTiming(0, { duration: 250 });
    }
  }, [isVisible, height, translateY, opacity]);

  const dismissKeyboard = () => {
    Keyboard?.dismiss();
  };

  const panGesture = Gesture.Pan()
    .onChange((event) => {
      if (event.translationY > 0) {
        gestureY.value = event.translationY;
      }
    })
    .onEnd((event) => {
      const { translationY, velocityY } = event;

      // If dragged down more than 100px or fast velocity downward, close
      if (translationY > 100 || velocityY > 500) {
        // Dismiss keyboard first if visible
        runOnJS(dismissKeyboard)();

        // Animate bottom sheet down first, then call onClose
        translateY.value = withTiming(height, { duration: 250 }, () => {
          runOnJS(onClose)();
        });
        opacity.value = withTiming(0, { duration: 250 });
        gestureY.value = withTiming(0, { duration: 250 });
        keyboardOffset.value = withTiming(0, { duration: 250 });
      } else {
        // Snap back to original position
        gestureY.value = withSpring(0);
      }
    });

  const handleBackdropPress = () => {
    if (enableBackdropDismiss) {
      // Dismiss keyboard first if visible
      if(Keyboard.isVisible()) {
        Keyboard?.dismiss();
        return;
      }

      // Animate bottom sheet down first, then call onClose
      translateY.value = withTiming(height, { duration: 250 }, () => {
        runOnJS(onClose)();
      });
      opacity.value = withTiming(0, { duration: 250 });
      gestureY.value = withTiming(0, { duration: 250 });
      keyboardOffset.value = withTiming(0, { duration: 250 });
    }
  };

  const handleClosePress = () => {
    // Dismiss keyboard first if visible
    Keyboard?.dismiss();

    // Animate bottom sheet down first, then call onClose
    translateY.value = withTiming(height, { duration: 250 }, () => {
      runOnJS(onClose)();
    });
    opacity.value = withTiming(0, { duration: 250 });
    gestureY.value = withTiming(0, { duration: 250 });
    keyboardOffset.value = withTiming(0, { duration: 250 });
  };

  const backdropStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const bottomSheetStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: translateY.value + keyboardOffset.value },
      { translateY: gestureY.value + keyboardOffset.value },
      // { translateY: keyboardOffset.value },
    ],
  }));

  if (!isVisible) {
    return null;
  }

  const ContentWrapper = scrollable ? ScrollView : View;
  const contentProps = scrollable
    ? {
        showsVerticalScrollIndicator: false,
        keyboardShouldPersistTaps: "handled" as const,
        contentContainerStyle: { flexGrow: 1 },
      }
    : { style: styles.content };

  return (
    <Modal
      transparent
      visible={isVisible}
      animationType="none"
      statusBarTranslucent
    >
      <View style={styles.container}>
        {/* Backdrop */}
        <TouchableWithoutFeedback onPress={handleBackdropPress}>
          <Animated.View style={[styles.backdrop, backdropStyle]} />
        </TouchableWithoutFeedback>

        {/* Bottom Sheet */}
        <GestureDetector gesture={enableSwipeDown ? panGesture : Gesture.Pan()}>
          <Animated.View
            style={[styles.bottomSheet, { height }, bottomSheetStyle]}
          >
            {/* Handle */}
            <View style={styles.handle} />

            {title && (
              <View style={styles.header}>
                <Text style={styles.title}>{title}</Text>
                <TouchableOpacity onPress={handleClosePress}>
                  <AntDesign name="close" size={24} color={Colors.border} />
                </TouchableOpacity>
              </View>
            )}

            {/* Content */}
            <ContentWrapper {...contentProps}>{children}</ContentWrapper>
          </Animated.View>
        </GestureDetector>
      </View>
    </Modal>
  );
};

export default BottomSheet;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "flex-end",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  bottomSheet: {
    backgroundColor: "#FFFFFF",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 34, // Safe area bottom
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  handle: {
    width: 40,
    height: 4,
    backgroundColor: "#D1D5DB",
    borderRadius: 2,
    alignSelf: "center",
    marginBottom: 16,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 21,
    fontWeight: "600",
  },
  close: {
    fontSize: 16,
  },
});
